import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { adsSpyService } from "../services/adsSpy.service";
import { useAdsSpyStore } from "../store/adsSpyStore";
import { SCRAPE_STATUS } from "../types/adsSpy.types";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface ScrapeAllModalProps {
  open: boolean;
  onClose: () => void;
}

interface BrandResult {
  name: string;
  success: boolean;
  inserted?: number;
  duplicates?: number;
}

export default function ScrapeAllModal({ open, onClose }: ScrapeAllModalProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const { brands, loadBrands, loadAds } = useAdsSpyStore();
  const [scraping, setScraping] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentBrand, setCurrentBrand] = useState("");
  const [results, setResults] = useState<BrandResult[]>([]);
  const [done, setDone] = useState(false);

  const activeBrands = brands.filter((b) => b.is_active);

  const handleScrapeAll = async () => {
    if (activeBrands.length === 0) {
      toast(t("no-active-brands", "No active brands"));
      return;
    }

    setScraping(true);
    setDone(false);
    setTotal(activeBrands.length);
    setCurrent(0);
    setResults([]);

    let totalNew = 0;
    const brandResults: BrandResult[] = [];

    for (const brand of activeBrands) {
      setCurrentBrand(brand.name);
      try {
        await adsSpyService.scrapeBrand(brand.id);
        // Poll until this brand finishes
        let finished = false;
        while (!finished) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const status = await adsSpyService.getScrapeStatus(brand.id);
            if (status.status === SCRAPE_STATUS.COMPLETED) {
              const newAds = status.total_ads - (brand.ad_count || 0);
              totalNew += Math.max(0, newAds);
              brandResults.push({
                name: brand.name,
                success: true,
                inserted: Math.max(0, newAds),
                duplicates: 0,
              });
              finished = true;
            } else if (status.status === SCRAPE_STATUS.FAILED) {
              brandResults.push({ name: brand.name, success: false });
              finished = true;
            }
          } catch {
            // ignore polling errors, retry
          }
        }
      } catch {
        brandResults.push({ name: brand.name, success: false });
      }
      setCurrent((c) => c + 1);
      setResults([...brandResults]);
    }

    setDone(true);
    setScraping(false);
    setCurrentBrand("");

    const errors = brandResults.filter((r) => !r.success);
    if (errors.length > 0) {
      toast(t("search-n-new-ads-n-errors", "Search: {new} new ads, {errors} with error").replace("{new}", String(totalNew)).replace("{errors}", String(errors.length)));
    } else {
      toast.success(t("search-completed-n-new", "Search completed: {n} new ads").replace("{n}", String(totalNew)));
    }

    await loadBrands();
    loadAds(true);
  };

  const handleClose = () => {
    if (scraping) return;
    setDone(false);
    setResults([]);
    setCurrent(0);
    onClose();
  };

  const pct = total > 0 ? (current / total) * 100 : 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-8">
        {!scraping && !done && (
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#EEF0FF] flex items-center justify-center mb-5">
              <RefreshCw className="w-7 h-7 text-fluxi" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
              {t("bulk-search", "Bulk Search")}
            </h3>

            <p className="text-[15px] text-gray-700 leading-relaxed mb-2">
              {t("will-search-new-ads-in-n-brands", "New ads will be searched in {n} active brand(s).").replace("{n}", String(activeBrands.length))}
            </p>
            <p className="text-sm text-gray-500 leading-relaxed max-w-sm mb-8">
              {t("system-will-check-ad-library", "The system will check each brand's ad library and add those not yet in your collection.")}
            </p>

            <div className="flex items-center gap-3">
              <button
                onClick={handleClose}
                className="text-sm font-medium px-5 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {t("cancel", "Cancel")}
              </button>
              <button
                onClick={handleScrapeAll}
                className="text-sm font-semibold px-6 py-2.5 rounded-lg bg-fluxi text-white hover:bg-[#4545D8] transition-colors flex items-center gap-1.5"
              >
                <RefreshCw className="w-4 h-4" />
                {t("search-in-n-brands", "Search in {n} brand(s)").replace("{n}", String(activeBrands.length))}
              </button>
            </div>
          </div>
        )}

        {(scraping || done) && (
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#EEF0FF] flex items-center justify-center mb-5">
              {scraping ? (
                <Loader2 className="w-7 h-7 animate-spin text-fluxi" />
              ) : (
                <RefreshCw className="w-7 h-7 text-fluxi" />
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {done ? t("search-completed", "Search completed") : t("searching-ads", "Searching ads...")}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {t("n-of-n-brands", "{current} of {total} brands").replace("{current}", String(current)).replace("{total}", String(total))}
            </p>

            <div className="w-full rounded-full h-2 bg-gray-100 mb-3">
              <div
                className="rounded-full h-2 transition-all duration-300"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg, #4368E5, #4DB5FF)" }}
              />
            </div>

            {scraping && currentBrand && (
              <p className="text-sm text-gray-500 mb-2">
                {t("searching-in", "Searching in:")} <span className="font-medium text-gray-700">{currentBrand}</span>
              </p>
            )}

            {results.length > 0 && done && (
              <div className="w-full mt-4 space-y-1.5 text-left">
                <div className="text-sm font-semibold text-green-700 mb-2 text-center">
                  {t("n-new-ads-from-n-brands", "{ads} new ads from {brands} brands")
                    .replace("{ads}", String(results.reduce((s, r) => s + (r.inserted || 0), 0)))
                    .replace("{brands}", String(results.length))}
                </div>
                {results.map((r, i) => (
                  <div
                    key={i}
                    className="text-sm flex justify-between text-gray-600"
                  >
                    <span>{r.name}</span>
                    <span
                      style={{
                        color: r.success ? "#1A8754" : "#E04040",
                      }}
                    >
                      {r.success
                        ? t("n-new", "+{n} new").replace("{n}", String(r.inserted))
                        : t("error", "Error")}
                    </span>
                  </div>
                ))}
                <button
                  onClick={handleClose}
                  className="w-full mt-5 py-2.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  {t("close", "Close")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
